import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/Layout/PageLayout';
import ContentBlock from '@/components/molecules/ContentBlock';
import { EnvironmentalDataCard } from '@/components/organisms/EnvironmentalDataCard';
import LatestImageCard from '@/components/organisms/LatestImageCard';

import CageHealthOverviewStatus from './components/CageHealthOverviewStatus/CageHealthOverviewStatus';
import CageStatusCard from './components/CageStatusCard';
import CardSlot from './components/CardSlot';
import LiceCard from './components/LiceCard';
import ModuleCardSkeleton from './components/ModuleCardSkeleton';
import OverviewExport from './components/OverviewExport';
import QualityEstimateCard from './components/QualityEstimateCard';
import WeightAndGrowthCard from './components/WeightAndGrowthCard';
import WelfareSummaryCard from './components/WelfareSummaryCard';
import type { CageDashboardViewProps } from './types';

import styles from './CageDashboard.module.scss';

/**
 * The cage's landing page: one card per module, each loading on its own.
 *
 * Smolt production shares the layout. It drops the lice and quality cards, which do not apply
 * to smolt, so the module cards stack in a single column instead of two.
 *
 * Cards that depend on the pen — a licence, the bioscope type, sensor data — sit in a
 * `CardSlot`: a skeleton until that is known, then the card, or a fade-out if it does not apply.
 */
export default function CageDashboardView(props: CageDashboardViewProps) {
    const {
        title,
        speciesLabel,
        isSmolt,
        isCageContextLoading,
        urls,
        overview,
        isOverviewLoading,
        weightUnitTag,
        daypartTag,
        lice,
        welfare,
        health,
        quality,
        detections,
        cageStatus,
    } = props;
    const { t } = useTranslation();

    return (
        <PageLayout
            title={title}
            subtitle={t('cageDashboard.cageDashboard.overview')}
            badge={speciesLabel}
            actions={
                <OverviewExport
                    overview={overview}
                    daypartTag={daypartTag}
                    disabled={isOverviewLoading}
                />
            }>
            <div className={styles.pageBoxLayout}>
                <div className={styles.mainSection}>
                    <WeightAndGrowthCard
                        estimatedWeight={overview.biomass.estimate.estimatedWeight}
                        period={overview.biomass.period}
                        weightUnitTag={weightUnitTag}
                        isLoading={isOverviewLoading}
                        weightUrl={urls.weight}
                    />

                    <div className={styles.moduleColumns} data-columns={isSmolt ? 1 : 2}>
                        {/* Smolt has neither card, so the column empties and hides itself. */}
                        <div className={styles.column}>
                            <CardSlot
                                isResolving={isCageContextLoading}
                                isVisible={quality.show}
                                placeholder={
                                    <QualityEstimateCard
                                        downgradeRate={undefined}
                                        lastMeasurement={null}
                                        isLoading
                                    />
                                }>
                                <QualityEstimateCard
                                    downgradeRate={quality.downgradeRate}
                                    lastMeasurement={quality.lastMeasurement}
                                    isLoading={quality.isLoading}
                                    loadError={quality.loadError}
                                />
                            </CardSlot>
                            <CardSlot
                                isResolving={isCageContextLoading}
                                isVisible={!isSmolt}
                                placeholder={
                                    <LiceCard
                                        lice={{ ...lice, isLoading: true }}
                                        liceUrl={urls.lice}
                                    />
                                }>
                                <LiceCard lice={lice} liceUrl={urls.lice} />
                            </CardSlot>
                        </div>

                        <div className={styles.column}>
                            {/* Welfare for a B/W bioscope, health for a colour one; which, if
                                either, is only known once the cage context has resolved. */}
                            <CardSlot
                                isResolving={isCageContextLoading}
                                isVisible={welfare.show || health.show}
                                placeholder={<ModuleCardSkeleton />}>
                                {welfare.show && (
                                    <WelfareSummaryCard
                                        welfare={welfare.data}
                                        swimSpeed={welfare.swimSpeed}
                                        isSmolt={isSmolt}
                                        hasSpeedAccess={welfare.hasSpeedAccess}
                                        isLoading={welfare.isLoading}
                                        loadError={welfare.loadError}
                                        welfareUrl={urls.welfare}
                                        woundsUrl={urls.wounds}
                                        swimSpeedUrl={urls.swimSpeed}
                                    />
                                )}
                                {health.show && (
                                    <ContentBlock spacing="none">
                                        <CageHealthOverviewStatus
                                            overview={health.data}
                                            healthUrl={urls.health}
                                            isLoading={health.isLoading}
                                            loadError={health.loadError}
                                        />
                                    </ContentBlock>
                                )}
                            </CardSlot>
                            {/* A cage without environmental sensors has no card at all. */}
                            <CardSlot
                                isResolving={isOverviewLoading && !overview.environmental}
                                isVisible={overview.environmental != null}
                                placeholder={
                                    <ContentBlock spacing="none">
                                        <EnvironmentalDataCard isLoading />
                                    </ContentBlock>
                                }>
                                <ContentBlock spacing="none">
                                    <EnvironmentalDataCard
                                        {...overview.environmental}
                                        isLoading={isOverviewLoading}
                                    />
                                </ContentBlock>
                            </CardSlot>
                        </div>
                    </div>
                </div>

                <div className={styles.sidebar}>
                    <LatestImageCard
                        title={t('cageDashboard.common.todaysCatch')}
                        imageAlt={t('cageDashboard.common.todaysCatch')}
                        images={detections.images}
                        isLoading={detections.isLoading}
                        loadError={detections.loadError}
                        url={urls.images}
                        className={styles.imageCard}
                    />
                    <CageStatusCard
                        cageStatus={cageStatus.data}
                        measurementCount={overview.biomass.period.measCnt ?? 0}
                        isLoading={cageStatus.isLoading || isOverviewLoading}
                        isOptoScaleAdmin={cageStatus.isOptoScaleAdmin}
                        isSmolt={isSmolt}
                        showBioscopeInspectionLink={cageStatus.showInspectionLink}
                        loadError={cageStatus.loadError}
                        cageUrl={urls.cage}
                    />
                </div>
            </div>
        </PageLayout>
    );
}
